<template>
  <div class="carrossel-wizard page">
    <Panel lineColor="var(--primary-color)">
      <CarrosselWizardStepper
        :steps="wizardSteps"
        :current-step="currentStep"
        :completed-steps="completedSteps"
      />

      <div v-if="loading" class="wizard-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <template v-else>
        <CarrosselStepInformacoes
          v-if="currentStep === 1"
          ref="stepInformacoes"
          :wizard-state="wizardState"
          :setores-folha="setoresFolha"
          :paginas-estaticas="paginasEstaticas"
          :product-editor-mode="productEditorMode"
          @dirty="markDirty"
        />

        <CarrosselStepBanners
          v-else-if="currentStep === 2 && !productEditorMode"
          ref="stepBanners"
          :wizard-state="wizardState"
          :setores-folha="setoresFolha"
          :paginas-estaticas="paginasEstaticas"
          :paginas-navegacao="paginasNavegacao"
          :show-componente="showComponente"
          @dirty="markDirty"
          @slide-deleted="onSessionSlideDeleted"
          @slides-added="onSessionSlidesAdded"
        />

        <CarrosselStepConteudoVitrine
          v-else-if="currentStep === 2 && productEditorMode"
          ref="stepConteudo"
          :wizard-state="wizardState"
          :setores-folha="setoresFolha"
          :show-componente="showComponente"
          :carousel-type-variant="carouselTypeVariant"
          @dirty="markDirty"
        />

        <CarrosselStepPublico
          v-else-if="currentStep === 3"
          ref="stepPublico"
          :wizard-state="wizardState"
          :segmentos-cliente="segmentosCliente"
          :show-componente="showComponente"
          :product-editor-mode="productEditorMode"
          @dirty="markDirty"
        />

        <CarrosselStepAparencia
          v-else-if="currentStep === 4"
          ref="stepAparencia"
          :wizard-state="wizardState"
          :product-editor-mode="productEditorMode"
          :hide-banner-preview="productEditorMode"
          :show-componente="showComponente"
          @dirty="markDirty"
        />

        <CarrosselStepRevisao
          v-else-if="currentStep === 5"
          ref="stepRevisao"
          :wizard-state="wizardState"
          :setores-folha="setoresFolha"
          :segmentos-cliente="segmentosCliente"
          :paginas-estaticas="paginasEstaticas"
          :product-editor-mode="productEditorMode"
          :carousel-type-variant="carouselTypeVariant"
          @edit-step="onEditStep"
        />
      </template>

      <div class="wizard-footer">
        <FormButton
          text="Cancelar"
          type="perigo"
          before-icon="fas fa-times"
          @click="onCancel"
        />
        <div class="wizard-footer__actions">
          <FormButton
            text="Voltar"
            type="secundario"
            before-icon="fas fa-arrow-left"
            :disabled="currentStep <= 1 || saving"
            @click="onBack"
          />
          <FormButton
            :text="continueLabel"
            type="primario"
            :after-icon="
              currentStep < 5 ? 'fas fa-chevron-right' : 'fas fa-check'
            "
            :loading="saving"
            @click="onContinue"
          />
        </div>
      </div>
    </Panel>
  </div>
</template>

<script>
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import CarrosselWizardStepper from "./CarrosselWizardStepper.vue";
import CarrosselStepInformacoes from "./steps/CarrosselStepInformacoes.vue";
import CarrosselStepBanners from "./steps/CarrosselStepBanners.vue";
import CarrosselStepConteudoVitrine from "./steps/CarrosselStepConteudoVitrine.vue";
import CarrosselStepPublico from "./steps/CarrosselStepPublico.vue";
import CarrosselStepAparencia from "./steps/CarrosselStepAparencia.vue";
import CarrosselStepRevisao from "./steps/CarrosselStepRevisao.vue";
import { SEGMENTO_LIST, SEGMENTO_CLIENTE_LIST } from "@/store/actions/segmento";
import { TODOS_SETORES } from "@/store/actions/setor";
import { PAGINA_ESTATICA_LIST } from "@/store/actions/paginaEstatica";
import {
  CARROSSEL_GET,
  CARROSSEL_CREATE,
  CARROSSEL_UPDATE
} from "@/store/actions/carrossel";
import {
  buildCarrosselPayload,
  mergeCreatedCarrosselIntoWizard,
  mergeSlidesPreservingActions,
  cloneWizardSnapshot,
  restoreWizardFromSnapshot,
  buildRollbackPayload,
  hasIrreversibleBannerChanges,
  CANCEL_WITH_IRREVERSIBLE_BANNER_MESSAGE,
  createEmptyAppearanceState,
  createEmptyConteudoState,
  createEmptyWizardCarrossel,
  getNavigationPages,
  getWizardSteps,
  hydrateAppearanceState,
  hydrateConteudoState,
  hydratePublicoState,
  parseLocaleExibicao
} from "../utils/utilCarrossel";
import {
  listarBannersPeriodoForaDaVigencia,
  mensagemBannersForaDaVigencia
} from "../utils/carrossel-slide-programacao";
import { mapGetters } from "vuex";

export default {
  name: "CarrosselWizard",
  components: {
    Panel,
    FormButton,
    CarrosselWizardStepper,
    CarrosselStepInformacoes,
    CarrosselStepBanners,
    CarrosselStepConteudoVitrine,
    CarrosselStepPublico,
    CarrosselStepAparencia,
    CarrosselStepRevisao
  },
  props: {
    tipoConteudo: {
      type: String,
      default: "STATIC_IMAGES"
    },
    productEditorMode: {
      type: Boolean,
      default: false
    },
    carouselTypeVariant: {
      type: String,
      default: "type1"
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      isDirty: false,
      currentStep: 1,
      completedSteps: [],
      originalLocalExibicao: [],
      committedSnapshot: null,
      sessionDeletedSlideIds: [],
      sessionAddedSlideIds: [],
      wizardState: {
        tipoConteudo: "STATIC_IMAGES",
        selectedLocalExibicao: [],
        showStaticPages: false,
        presentOnStaticPage: [],
        showCategorias: false,
        selectedCategorias: [],
        segmentoClienteSelecionado: null,
        slideImageRevisions: {},
        conteudoState: createEmptyConteudoState(),
        appearanceState: createEmptyAppearanceState(),
        carrossel: createEmptyWizardCarrossel("STATIC_IMAGES")
      },
      setoresFolha: [],
      paginasEstaticas: [],
      segmentosCliente: [],
      paginasNavegacao: getNavigationPages()
    };
  },
  computed: {
    ...mapGetters(["getFornecedorId", "moduloFornecedorAtivo"]),
    wizardSteps() {
      return getWizardSteps(this.tipoConteudo);
    },
    isEditMode() {
      return Boolean(this.$route.params.idcarrossel);
    },
    listRoute() {
      return (
        this.$route.meta?.listRoute || "/fornecedor/customizacao/carrossel"
      );
    },
    continueLabel() {
      if (this.currentStep >= 5) {
        return "Salvar e Finalizar";
      }
      return "Salvar e continuar";
    },
    entityLabel() {
      return this.productEditorMode ? "vitrine" : "carrossel";
    }
  },
  async created() {
    this.wizardState.tipoConteudo = this.tipoConteudo;
    this.wizardState.carrossel = createEmptyWizardCarrossel(this.tipoConteudo);
    this.wizardState.conteudoState = createEmptyConteudoState();
    this.loading = true;

    try {
      await Promise.all([
        this.loadSetores(),
        this.loadPaginasEstaticas(),
        this.loadSegmentosCliente()
      ]);

      if (this.isEditMode) {
        await this.loadCarrossel(this.$route.params.idcarrossel);
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    showComponente(modules) {
      return this.moduloFornecedorAtivo(modules);
    },
    markDirty() {
      this.isDirty = true;
    },
    captureCommittedSnapshot() {
      this.committedSnapshot = cloneWizardSnapshot(this.wizardState);
      this.sessionDeletedSlideIds = [];
      this.sessionAddedSlideIds = [];
    },
    onSessionSlideDeleted(slideId) {
      if (!slideId) return;

      if (!this.sessionDeletedSlideIds.includes(slideId)) {
        this.sessionDeletedSlideIds.push(slideId);
      }

      this.sessionAddedSlideIds = this.sessionAddedSlideIds.filter(
        id => id !== slideId
      );
    },
    onSessionSlidesAdded(slideIds = []) {
      slideIds.filter(Boolean).forEach(slideId => {
        if (!this.sessionAddedSlideIds.includes(slideId)) {
          this.sessionAddedSlideIds.push(slideId);
        }
      });
    },
    async rollbackToCommittedSnapshot() {
      if (!this.committedSnapshot?.carrossel?.idcarrossel) {
        return;
      }

      const payload = buildRollbackPayload(
        this.committedSnapshot,
        this.sessionDeletedSlideIds
      );
      await this.$store.dispatch(CARROSSEL_UPDATE, {
        idcarrossel: this.committedSnapshot.carrossel.idcarrossel,
        configs: payload
      });

      const rollbackSnapshot = cloneWizardSnapshot(this.committedSnapshot);
      if (rollbackSnapshot?.carrossel) {
        rollbackSnapshot.carrossel.slides = (
          rollbackSnapshot.carrossel.slides || []
        ).filter(
          slide => slide?.id && !this.sessionDeletedSlideIds.includes(slide.id)
        );
      }

      restoreWizardFromSnapshot(this.wizardState, rollbackSnapshot);
      this.sessionDeletedSlideIds = [];
      this.sessionAddedSlideIds = [];
    },
    async loadSetores() {
      const segmentos = await this.$store.dispatch(SEGMENTO_LIST);
      const idSegmentos = segmentos.map(s => s.idsegmento);
      const folhas = [];

      for (const segmento of segmentos) {
        const setores = await this.$store.dispatch(
          TODOS_SETORES,
          segmento.idsegmento
        );
        folhas.push(
          ...setores.filter(f => idSegmentos.indexOf(f.idsegmento) >= 0)
        );
      }

      this.setoresFolha = folhas;
    },
    async loadPaginasEstaticas() {
      this.paginasEstaticas = await this.$store.dispatch(PAGINA_ESTATICA_LIST);
    },
    async loadSegmentosCliente() {
      const segmentos = await this.$store.dispatch(SEGMENTO_CLIENTE_LIST, {
        idfornecedor: this.getFornecedorId
      });
      this.segmentosCliente = (segmentos || []).map(item => ({
        text: item.nome || item.descricao,
        value: item.idsegmentocliente
      }));
    },
    hydrateWizardFromCarrossel(carrossel) {
      const appearanceState = hydrateAppearanceState(carrossel);
      const publicoState = hydratePublicoState(
        carrossel,
        this.segmentosCliente
      );

      this.wizardState.appearanceState = {
        ...appearanceState,
        userLoginType: publicoState.userLoginType
      };
      this.wizardState.segmentoClienteSelecionado =
        publicoState.segmentoClienteSelecionado;

      if (carrossel.config?.showOnPesquisaAtivada != null) {
        this.wizardState.carrossel.config.showOnPesquisaAtivada =
          carrossel.config.showOnPesquisaAtivada;
      }
    },
    async loadCarrossel(idcarrossel) {
      const carrossel = await this.$store.dispatch(CARROSSEL_GET, idcarrossel);
      const parsed = parseLocaleExibicao(
        carrossel.localexibicao,
        this.setoresFolha
      );

      this.originalLocalExibicao = [...(carrossel.localexibicao || [])];

      this.wizardState = {
        ...this.wizardState,
        tipoConteudo: carrossel.tipo_conteudo || this.tipoConteudo,
        selectedLocalExibicao: parsed.selectedLocalExibicao,
        showStaticPages: parsed.showStaticPages,
        presentOnStaticPage: carrossel.config?.presentOnStaticPage || [],
        showCategorias: parsed.showCategorias,
        selectedCategorias: parsed.selectedCategorias || [],
        slideImageRevisions: {},
        carrossel: {
          ...createEmptyWizardCarrossel(this.tipoConteudo),
          ...carrossel,
          config: {
            ...createEmptyWizardCarrossel(this.tipoConteudo).config,
            ...(carrossel.config || {})
          },
          slides: carrossel.slides || []
        }
      };

      this.hydrateWizardFromCarrossel(this.wizardState.carrossel);

      if (this.productEditorMode) {
        hydrateConteudoState(
          this.wizardState,
          this.setoresFolha,
          this.carouselTypeVariant
        );
      }

      this.captureCommittedSnapshot();
    },
    async onCancel() {
      if (this.isDirty) {
        const hasIrreversibleChanges = hasIrreversibleBannerChanges(
          this.wizardState,
          this.sessionDeletedSlideIds
        );
        const cancelMessage = hasIrreversibleChanges
          ? CANCEL_WITH_IRREVERSIBLE_BANNER_MESSAGE
          : {
              title: "Alterações não salvas",
              text: "Existem alterações não salvas. Deseja sair mesmo assim?"
            };

        const result = await this.$swal.fire({
          title: cancelMessage.title,
          text: cancelMessage.text,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Sair",
          cancelButtonText: "Cancelar",
          reverseButtons: true
        });
        if (!result.value) return;

        if (this.committedSnapshot?.carrossel?.idcarrossel) {
          try {
            await this.rollbackToCommittedSnapshot();
          } catch (err) {
            const message =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              "Erro ao descartar alterações";
            this.$vueOnToast.pop("error", message);
            return;
          }
        }
      }
      this.$router.push(this.listRoute);
    },
    onBack() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    onEditStep(step) {
      if (step >= 1 && step <= 4) {
        this.currentStep = step;
      }
    },
    async onContinue() {
      if (this.currentStep === 1) {
        await this.saveStepInformacoes();
        return;
      }

      if (this.currentStep === 2) {
        if (this.productEditorMode) {
          await this.saveStepConteudo();
        } else {
          await this.saveStepBanners();
        }
        return;
      }

      if (this.currentStep === 3) {
        await this.saveStepPublico();
        return;
      }

      if (this.currentStep === 4) {
        await this.saveStepAparencia();
        return;
      }

      if (this.currentStep === 5) {
        await this.publishCarrossel();
      }
    },
    vigenciaCompativelComBanners() {
      if (this.productEditorMode) return true;

      const bannersFora = listarBannersPeriodoForaDaVigencia(
        this.wizardState.carrossel?.slides,
        {
          inicio: this.wizardState.carrossel?.inicio_vigencia,
          fim: this.wizardState.carrossel?.fim_vigencia
        }
      );

      if (!bannersFora.length) return true;

      this.$vueOnToast.pop(
        "warning",
        mensagemBannersForaDaVigencia(bannersFora)
      );
      return false;
    },
    async persistCarrossel() {
      try {
        const payload = buildCarrosselPayload(this.wizardState);
        const updated = await this.$store.dispatch(CARROSSEL_UPDATE, {
          idcarrossel: this.wizardState.carrossel.idcarrossel,
          configs: payload
        });

        this.wizardState.carrossel = {
          ...this.wizardState.carrossel,
          ...updated,
          config: {
            ...this.wizardState.carrossel.config,
            ...(updated.config || {})
          },
          slides: mergeSlidesPreservingActions(
            this.wizardState.carrossel.slides,
            updated.slides || payload.slides || []
          )
        };

        this.captureCommittedSnapshot();

        return updated;
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Erro ao salvar";
        this.$vueOnToast.pop("error", message);
        return null;
      }
    },
    async saveStepInformacoes() {
      const step = this.$refs.stepInformacoes;
      if (!step?.validate()) return;

      step.syncToCarrossel();
      if (!this.vigenciaCompativelComBanners()) return;

      const localChanged =
        this.isEditMode &&
        JSON.stringify(this.originalLocalExibicao) !==
          JSON.stringify(this.wizardState.carrossel.localexibicao);

      if (localChanged) {
        const result = await this.$swal.fire({
          title: "Alterar local de exibição",
          text: `Alterar o local de exibição pode impactar onde ${this.entityLabel} aparece. Deseja continuar?`,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          reverseButtons: true
        });
        if (!result.value) return;
      }

      this.saving = true;

      try {
        if (!this.isEditMode && !this.wizardState.carrossel.idcarrossel) {
          const created = await this.$store.dispatch(CARROSSEL_CREATE, {
            title: (this.wizardState.carrossel.titulo || "").trim(),
            tipoConteudo: this.tipoConteudo
          });

          this.wizardState.carrossel = mergeCreatedCarrosselIntoWizard(
            this.wizardState.carrossel,
            created
          );
        }

        const saved = await this.persistCarrossel();
        if (!saved) return;

        this.originalLocalExibicao = [
          ...(this.wizardState.carrossel.localexibicao || [])
        ];
        this.markStepCompleted(1);
        this.currentStep = 2;
        this.isDirty = false;

        this.$vueOnToast.pop("success", "Informações salvas");
      } finally {
        this.saving = false;
      }
    },
    async saveStepConteudo() {
      const step = this.$refs.stepConteudo;
      if (!step?.validate()) return;

      step.syncToCarrossel();
      this.saving = true;

      try {
        const saved = await this.persistCarrossel();
        if (!saved) return;

        this.markStepCompleted(2);
        this.currentStep = 3;
        this.isDirty = false;
        this.$vueOnToast.pop("success", "Conteúdo salvo");
      } finally {
        this.saving = false;
      }
    },
    async saveStepBanners() {
      const step = this.$refs.stepBanners;
      if (!step?.validate()) return;

      this.saving = true;

      try {
        const saved = await this.persistCarrossel();
        if (!saved) return;

        this.markStepCompleted(2);
        this.currentStep = 3;
        this.isDirty = false;
        this.$vueOnToast.pop("success", "Banners salvos");
      } finally {
        this.saving = false;
      }
    },
    async saveStepPublico() {
      const step = this.$refs.stepPublico;
      if (!step?.validate()) return;

      step.syncToCarrossel();
      this.saving = true;

      try {
        const saved = await this.persistCarrossel();
        if (!saved) return;

        this.markStepCompleted(3);
        this.currentStep = 4;
        this.isDirty = false;
        this.$vueOnToast.pop("success", "Público salvo");
      } finally {
        this.saving = false;
      }
    },
    async saveStepAparencia() {
      const step = this.$refs.stepAparencia;
      if (!step?.validate()) return;

      step.syncToCarrossel();
      this.saving = true;

      try {
        const saved = await this.persistCarrossel();
        if (!saved) return;

        this.markStepCompleted(4);
        this.currentStep = 5;
        this.isDirty = false;
        this.$vueOnToast.pop("success", "Aparência salva");
      } finally {
        this.saving = false;
      }
    },
    async publishCarrossel() {
      const step = this.$refs.stepRevisao;
      if (!step?.validate()) return;
      if (!this.vigenciaCompativelComBanners()) return;

      this.saving = true;

      try {
        const ativoAnterior = this.wizardState.carrossel.ativo;
        this.wizardState.carrossel.ativo = true;
        const saved = await this.persistCarrossel();
        if (!saved) {
          this.wizardState.carrossel.ativo = ativoAnterior;
          return;
        }

        this.markStepCompleted(5);
        this.isDirty = false;
        this.$vueOnToast.pop(
          "success",
          `${
            this.productEditorMode ? "Vitrine" : "Carrossel"
          } publicado com sucesso`
        );
        this.$router.push(this.listRoute);
      } finally {
        this.saving = false;
      }
    },
    markStepCompleted(stepId) {
      if (!this.completedSteps.includes(stepId)) {
        this.completedSteps.push(stepId);
      }
    }
  }
};
</script>

<style scoped>
.wizard-loading {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.wizard-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #eee;
}

.wizard-footer__actions {
  display: flex;
  gap: 12px;
}
</style>
